// hooks/useTableDensity.ts
import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useSettings } from '../Settings/SettingsProvider';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';

export interface TableDensityClasses {
  tableCell: string;
  tableHeader: string;
  cellContent: string;
  button: string;
  text: string;
  cardHeader: string;
  icon: {
    small: string;
    medium: string;
    large: string;
  };
  truncation: {
    short: string;
    medium: string;
    long: string;
  };
}

// Reusable truncated cell component with smart tooltip (only shows when actually truncated)
interface TruncatedCellProps {
  children: React.ReactNode;
  text: string;
  className?: string;
}

export const TruncatedCell = ({
  children,
  text,
  className,
}: TruncatedCellProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const isOverflowing =
          textRef.current.scrollWidth > textRef.current.clientWidth;
        setShowTooltip(isOverflowing);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [text]);

  const textElement = (
    <div ref={textRef} className={cn('truncate w-full', className)}>
      {children}
    </div>
  );

  if (showTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{textElement}</TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs break-words">{text}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return textElement;
};

// Date/DateTime cell component that never truncates
interface DateTimeCellProps {
  children: React.ReactNode;
  className?: string;
}

export const DateTimeCell = ({ children, className }: DateTimeCellProps) => {
  return <div className={cn('whitespace-nowrap', className)}>{children}</div>;
};

// Numeric cell component that never truncates and right-aligns
interface NumericCellProps {
  children: React.ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
}

export const NumericCell = ({
  children,
  className,
  align = 'right',
}: NumericCellProps) => {
  const alignmentClass = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
  }[align];

  return (
    <div className={cn('whitespace-nowrap', alignmentClass, className)}>
      {children}
    </div>
  );
};

// Short text cell that displays without truncation
interface ShortTextCellProps {
  children: React.ReactNode;
  className?: string;
}

export const ShortTextCell = ({ children, className }: ShortTextCellProps) => {
  return <div className={cn('', className)}>{children}</div>;
};

export const useTableDensity = () => {
  const { settings } = useSettings();

  const tableDensity: 'normal' | 'compact' = useMemo(
    () => settings['ribbonPlugin.settings']?.table_density || 'normal',
    [settings],
  );

  const densityClasses: TableDensityClasses = useMemo(
    () => ({
      tableCell: tableDensity === 'compact' ? 'py-0 px-2' : 'py-1 px-4',
      tableHeader: tableDensity === 'compact' ? 'py-1 px-2' : 'py-2 px-4',
      cellContent: tableDensity === 'compact' ? 'py-0.5' : 'py-1',
      button: tableDensity === 'compact' ? 'size-6 p-0' : 'size-8 p-0',
      text: tableDensity === 'compact' ? 'text-xs' : 'text-sm',
      cardHeader:
        tableDensity === 'compact' ? 'px-4 py-2 pb-1' : 'px-7 py-4 pb-2',
      icon: {
        small: tableDensity === 'compact' ? 'size-3' : 'size-4',
        medium: tableDensity === 'compact' ? 'size-4' : 'size-5',
        large: tableDensity === 'compact' ? 'size-5' : 'size-6',
      },
      truncation: {
        short: tableDensity === 'compact' ? 'max-w-16' : 'max-w-24',
        medium: tableDensity === 'compact' ? 'max-w-20' : 'max-w-28',
        long: tableDensity === 'compact' ? 'max-w-24' : 'max-w-32',
      },
    }),
    [tableDensity],
  );

  return {
    tableDensity,
    densityClasses,
    // Cell components for consistent usage across all tables
    TruncatedCell,
    DateTimeCell,
    NumericCell,
    ShortTextCell,
  };
};
